import { compact, first, isEmpty } from 'lodash';

import { AISuggestionMatch } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { SURVIVING_SPOUSE_SENTINEL } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { defaultBeneficiary } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.defaults';
import {
  AccessParameterRadioGroupKind,
  AgeRequirementKind,
  BeneficiaryFormAccessParameter,
  BeneficiaryFormAccessParameterAgeParam,
  BeneficiaryFormAccessParameters,
  BeneficiaryFormPowerOfAppointment,
  BeneficiaryFormScheduledDistribution,
  BeneficiaryFormScheduledDistributions,
  DistributionType,
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesSubformShape,
  EntityBeneficiaryAccessFrequencies,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import { getAccessParameterKindFromSchedculedDistributionKind } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.utils';
import {
  AccessParameterKind,
  AiSuggestionKind,
  BeneficiaryPowerOfAppointmentPower,
  ScheduledDistributionFrequency,
  ScheduledDistributionKind,
} from '@/types/schema';
import { parseDecimalJSOrNull } from '@/utils/decimalJSUtils';

import {
  BeneficiaryAccessParameterFragment,
  BeneficiaryAgeParametersFragment,
  BeneficiaryScheduledDistributionFragment,
  BeneficiaryV2AiSuggestionFragment,
} from '../../graphql/aiSuggestions.generated';

/**
 * The beneficiary data type used by beneficiaryV2, subtrustLifetimeBeneficiary,
 * and subtrustRemainderBeneficiary. All three use InferredBeneficiaryDetailsV2.
 */
type BeneficiaryV2Data = NonNullable<
  BeneficiaryV2AiSuggestionFragment['beneficiaryV2']
>;

export function suggestionsToBeneficiariesV2Subform(
  matches: AISuggestionMatch[]
): NonNullable<EntityBeneficiariesSubformShape['beneficiaries']> {
  return compact(
    matches
      .filter((m) => m.clientProfileId || m.clientOrganizationId || m.entityId)
      .filter((m) => m.suggestion.kind === AiSuggestionKind.BeneficiaryV2)
      .filter((m) => m.suggestion.beneficiaryV2)
      .map((match) =>
        suggestionToBeneficiaryFromData(match, match.suggestion.beneficiaryV2!)
      )
  );
}

/**
 * Helper to create beneficiary subform mappers for different suggestion kinds.
 * All subtrust beneficiary kinds use the same InferredBeneficiaryDetailsV2 structure.
 */
function createSubtrustBeneficiaryMapper(
  kind: AiSuggestionKind,
  getField: (
    suggestion: AISuggestionMatch['suggestion']
  ) => BeneficiaryV2Data | null | undefined
) {
  return (
    matches: AISuggestionMatch[]
  ): NonNullable<EntityBeneficiariesSubformShape['beneficiaries']> => {
    return compact(
      matches
        .filter(
          (m) =>
            m.clientProfileId ||
            m.clientOrganizationId ||
            m.entityId ||
            m.survivingSpouse
        )
        .filter((m) => m.suggestion.kind === kind)
        .filter((m) => getField(m.suggestion))
        .map((match) =>
          suggestionToBeneficiaryFromData(match, getField(match.suggestion)!)
        )
    );
  };
}

/**
 * Maps SUBTRUST_LIFETIME_BENEFICIARY suggestions to beneficiary form values.
 * Used for the ACCESS_AFTER_DEATH section in testamentary entity modals.
 */
export const suggestionsToSubtrustLifetimeBeneficiariesSubform =
  createSubtrustBeneficiaryMapper(
    AiSuggestionKind.SubtrustLifetimeBeneficiary,
    (s) => s.subtrustLifetimeBeneficiary
  );

/**
 * Maps SUBTRUST_REMAINDER_BENEFICIARY suggestions to beneficiary form values.
 * Used for the ACCESS_AFTER_BOTH_DEATHS section in testamentary entity modals.
 */
export const suggestionsToSubtrustRemainderBeneficiariesSubform =
  createSubtrustBeneficiaryMapper(
    AiSuggestionKind.SubtrustRemainderBeneficiary,
    (s) => s.subtrustRemainderBeneficiary
  );

/**
 * Shared helper that converts beneficiary data (from beneficiaryV2,
 * subtrustLifetimeBeneficiary, or subtrustRemainderBeneficiary) to form values.
 * All three use the same InferredBeneficiaryDetailsV2 type.
 */
function suggestionToBeneficiaryFromData(
  suggestionMatch: AISuggestionMatch,
  beneficiaryData: BeneficiaryV2Data
): EntityBeneficiariesFormBeneficiary | null {
  const beneficiaryId = suggestionMatch.survivingSpouse
    ? SURVIVING_SPOUSE_SENTINEL
    : suggestionMatch.clientProfileId ||
      suggestionMatch.clientOrganizationId ||
      suggestionMatch.entityId;
  if (!beneficiaryId) {
    // This shouldn't happen since we pre-filtered the matches. This is for type safety.
    return null;
  }

  const inputAccessParameters: BeneficiaryAccessParameterFragment[] = compact(
    beneficiaryData.accessParameters
  );
  const inputScheduledDistributions: BeneficiaryScheduledDistributionFragment[] =
    compact(beneficiaryData.scheduledDistributions);

  const beneficiaryFormAccessParameters: BeneficiaryFormAccessParameters =
    accessParametersToBeneficiaryFormAccessParameters(
      inputAccessParameters,
      inputScheduledDistributions
    );

  return {
    id: null,
    beneficiaryId,
    level: beneficiaryData.order || '',
    notes: beneficiaryData.notes || '',
    beneficiaryFormScheduledDistributions:
      scheduledDistributionsToBeneficiaryFormScheduledDistributions(
        inputScheduledDistributions
      ),
    beneficiaryFormAccessParameters,
    beneficiaryFormPowerOfAppointment:
      powerOfAppointmentToBeneficiaryFormPowerOfAppointment(
        beneficiaryData.powerOfAppointment,
        beneficiaryData.powerOfAppointmentNotes
      ),
    isSuggestion: true,
  };
}

function mapBeneficiaryAccessParameterFormat(
  ap: BeneficiaryAccessParameterFragment | null | undefined
): BeneficiaryFormAccessParameter | null {
  if (!ap) return null;
  return {
    kind: ap.kind!,
    _hasFrequency: false,
    _hasAgeParams: hasAgeParameters(ap.accessAgeParameters),
    _hasAmountOrPercentage:
      !!ap.distributionAmount || !!ap.distributionPercentage,
    ageParams: ap.accessAgeParameters
      ? ageParametersToBeneficiaryFormAccessParameterAgeParams(
          ap.accessAgeParameters
        )
      : undefined,
    distributionType: DistributionType.Discretionary,
  };
}

function mapScheduleDistributionToAccessParameter(
  scheduledDistribution:
    | BeneficiaryScheduledDistributionFragment
    | null
    | undefined
): BeneficiaryFormAccessParameter | null {
  if (!scheduledDistribution) return null;

  const kind = getAccessParameterKindFromSchedculedDistributionKind(
    scheduledDistribution.kind
  );

  const ageParams: BeneficiaryFormAccessParameterAgeParam[] = [];
  if (scheduledDistribution.accessAgeParameters) {
    let ageRequirementKind:
      | BeneficiaryFormAccessParameterAgeParam['_ageRequirementKind']
      | null = null;
    const { ageRequirementStart, ageRequirementEnd } =
      scheduledDistribution.accessAgeParameters;
    if (ageRequirementStart && ageRequirementEnd) {
      ageRequirementKind = 'between';
    } else if (ageRequirementEnd) {
      ageRequirementKind = 'until';
    } else if (ageRequirementStart) {
      ageRequirementKind = 'upon';
    }
    if (ageRequirementKind) {
      ageParams.push({
        _ageRequirementKind: ageRequirementKind,
        ageRequirementEnd: ageRequirementEnd?.toString(),
        ageRequirementStart: ageRequirementStart?.toString(),
      });
    }
  }

  if (!kind) return null;

  const amount = parseDecimalJSOrNull(scheduledDistribution.distributionAmount);
  const percentage = parseDecimalJSOrNull(
    scheduledDistribution.distributionPercentage
  );

  const output: BeneficiaryFormAccessParameter = {
    kind,
    _hasFrequency:
      scheduledDistribution.distributionFrequency !==
      ScheduledDistributionFrequency.OneTime,
    frequency: scheduledDistribution.distributionFrequency ?? undefined,
    _hasAmountOrPercentage: !!amount || !!percentage,
    amount: amount as unknown as undefined, // HACK: we do this because expected type is OpaqueDecimal
    percentage: percentage as unknown as undefined, // HACK: we do this because expected type is OpaqueDecimal
    distributionType: DistributionType.Scheduled,
    _hasAgeParams: !isEmpty(ageParams),
    ageParams,
  };

  return output;
}

function accessParametersToBeneficiaryFormAccessParameters(
  accessParameters: BeneficiaryAccessParameterFragment[],
  scheduledDistributions: BeneficiaryScheduledDistributionFragment[]
): BeneficiaryFormAccessParameters {
  const beneficiaryAccessParameters: BeneficiaryFormAccessParameters =
    defaultBeneficiary().beneficiaryFormAccessParameters!;

  if (isEmpty(accessParameters) && isEmpty(scheduledDistributions)) {
    return beneficiaryAccessParameters;
  }

  // case 1: full access -- overrides all other access parameters if present
  if (accessParameters.some((ap) => ap.kind === AccessParameterKind.Full)) {
    return {
      ...beneficiaryAccessParameters,
      _accessParameterKind: AccessParameterRadioGroupKind.Full,
      kind: AccessParameterKind.Full,
    };
  }

  // case 2: partial access

  // map all percent/amount access parameters to the form
  const partialAccessParameters: BeneficiaryAccessParameterFragment[] =
    accessParameters.filter((ap) =>
      [AccessParameterKind.Percentage, AccessParameterKind.Amount].includes(
        ap.kind!
      )
    );

  beneficiaryAccessParameters.kind =
    first(partialAccessParameters)?.kind ?? AccessParameterKind.Amount;
  beneficiaryAccessParameters._accessParameterKind =
    AccessParameterRadioGroupKind.Partial;

  beneficiaryAccessParameters.amountsOrPercentages = compact(
    partialAccessParameters.map<BeneficiaryFormAccessParameter | null>((ap) => {
      if (!ap) return null;
      if (ap.kind == AccessParameterKind.Amount) {
        if (!ap.distributionAmount) return null;

        const amount = parseDecimalJSOrNull(ap.distributionAmount);

        return {
          kind: ap.kind,
          amount: amount as unknown as undefined, // HACK: we do this because expected type is OpaqueDecimal
          _hasAmountOrPercentage: !!amount,
          _hasFrequency: !!ap.distributionFrequency,
          frequency:
            ap.distributionFrequency ??
            EntityBeneficiaryAccessFrequencies.Ongoing,
          _hasAgeParams: hasAgeParameters(ap.accessAgeParameters),
          ageParams: ap.accessAgeParameters
            ? ageParametersToBeneficiaryFormAccessParameterAgeParams(
                ap.accessAgeParameters
              )
            : undefined,
          distributionType: DistributionType.Discretionary,
        };
      }
      if (ap.kind === AccessParameterKind.Percentage) {
        if (!ap.distributionPercentage) return null;
        // PERCENTAGE
        const percentage = parseDecimalJSOrNull(ap.distributionPercentage);
        return {
          kind: ap.kind,
          percentage: (percentage ?? undefined) as unknown as undefined, // HACK: we do this because expected type is OpaqueDecimal
          _hasAmountOrPercentage: !!percentage,
          _hasFrequency: !!ap.distributionFrequency,
          frequency:
            ap.distributionFrequency ??
            EntityBeneficiaryAccessFrequencies.Ongoing,
          _hasAgeParams: hasAgeParameters(ap.accessAgeParameters),
          ageParams: ap.accessAgeParameters
            ? ageParametersToBeneficiaryFormAccessParameterAgeParams(
                ap.accessAgeParameters
              )
            : undefined,
          distributionType: DistributionType.Discretionary,
        };
      }

      return null;
    })
  );

  compact(scheduledDistributions).forEach((scheduledDistribution) => {
    const mappedDistribution = mapScheduleDistributionToAccessParameter(
      scheduledDistribution
    );

    // handle the "All trust income" below
    if (
      mappedDistribution &&
      mappedDistribution.kind !== AccessParameterKind.AllTrustIncome
    ) {
      beneficiaryAccessParameters.amountsOrPercentages.push(mappedDistribution);
    }
  });

  beneficiaryAccessParameters._hasAmountOrPercentage = !isEmpty(
    beneficiaryAccessParameters.amountsOrPercentages
  );

  const fullDiscretionParam = mapBeneficiaryAccessParameterFormat(
    accessParameters.find(
      (ap) => ap.kind === AccessParameterKind.FullDiscretion
    )
  );
  if (fullDiscretionParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.FullDiscretion] =
      fullDiscretionParam;
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.FullDiscretion] =
      true;
  }

  const hmsParam = mapBeneficiaryAccessParameterFormat(
    accessParameters.find((ap) => ap.kind === AccessParameterKind.Hms)
  );
  if (hmsParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Hms] = hmsParam;
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Hms] = true;
  }

  const hemsParam = mapBeneficiaryAccessParameterFormat(
    accessParameters.find((ap) => ap.kind === AccessParameterKind.Hems)
  );
  if (hemsParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Hems] = hemsParam;
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Hems] = true;
  }

  const allTrustIncomeParam =
    mapBeneficiaryAccessParameterFormat(
      accessParameters.find(
        (ap) => ap.kind === AccessParameterKind.AllTrustIncome
      )
    ) ||
    mapScheduleDistributionToAccessParameter(
      scheduledDistributions?.find(
        (sd) => sd.kind === ScheduledDistributionKind.AllIncome
      )
    );
  if (allTrustIncomeParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.AllTrustIncome] =
      allTrustIncomeParam;
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.AllTrustIncome] =
      true;
  }

  const otherParam = mapBeneficiaryAccessParameterFormat(
    accessParameters.find((ap) => ap.kind === AccessParameterKind.Other)
  );
  if (otherParam) {
    beneficiaryAccessParameters.values[AccessParameterKind.Other] = otherParam;
    beneficiaryAccessParameters.checkboxes[AccessParameterKind.Other] = true;
  }
  return beneficiaryAccessParameters;
}

function scheduledDistributionsToBeneficiaryFormScheduledDistributions(
  scheduledDistributions: BeneficiaryScheduledDistributionFragment[]
): BeneficiaryFormScheduledDistributions {
  if (!scheduledDistributions || scheduledDistributions.length === 0) {
    return {
      _hasScheduledDistributions: false,
      scheduledDistributions: [],
    };
  }

  const scheduledDistributionsPartial: BeneficiaryFormScheduledDistribution[] =
    [];
  scheduledDistributions.forEach((sd) => {
    if (!sd.kind) {
      return;
    }
    const ageParams = sd.accessAgeParameters
      ? ageParametersToBeneficiaryFormAccessParameterAgeParams(
          sd.accessAgeParameters
        )
      : undefined;
    scheduledDistributionsPartial.push({
      _hasAgeParams: hasAgeParameters(sd.accessAgeParameters),
      _ageRequirementKind: ageParams?.[0]
        ? ageParams?.[0]!._ageRequirementKind
        : '',
      scheduledDistribution: {
        kind: sd.kind,
        amount: parseDecimalJSOrNull(sd.distributionAmount) ?? undefined,
        percentage:
          parseDecimalJSOrNull(sd.distributionPercentage) ?? undefined,
        frequency: sd.distributionFrequency ?? '',
        ageRequirementStart: ageParams?.[0]?.ageRequirementStart ?? '',
        ageRequirementEnd: ageParams?.[0]?.ageRequirementEnd ?? '',
        scheduledDistributionNotes: sd.notes || '',
      },
    });
  });
  return {
    _hasScheduledDistributions: true,
    scheduledDistributions: scheduledDistributionsPartial,
  };
}

function hasAgeParameters(
  params: BeneficiaryAgeParametersFragment | null | undefined
): boolean {
  if (params) {
    if (params.ageRequirementStart !== 0 || params.ageRequirementEnd !== 0) {
      return true;
    }
  }
  return false;
}

function ageParametersToBeneficiaryFormAccessParameterAgeParams(
  params: BeneficiaryAgeParametersFragment
): BeneficiaryFormAccessParameterAgeParam[] {
  let kind = '';
  if (params.ageRequirementStart !== 0 && params.ageRequirementEnd !== 0) {
    kind = 'between';
  } else if (params.ageRequirementStart !== 0) {
    kind = 'upon';
  } else if (params.ageRequirementEnd !== 0) {
    kind = 'until';
  }
  return [
    {
      _ageRequirementKind: kind as AgeRequirementKind,
      ageRequirementStart: params.ageRequirementStart?.toString() ?? '',
      ageRequirementEnd: params.ageRequirementEnd?.toString() ?? '',
      notes: params.notes ?? '',
    },
  ];
}

function powerOfAppointmentToBeneficiaryFormPowerOfAppointment(
  powerOfAppointment: BeneficiaryPowerOfAppointmentPower | null | undefined,
  powerOfAppointmentNotes: string | null | undefined
): BeneficiaryFormPowerOfAppointment {
  if (!powerOfAppointment) {
    return {
      _hasPowerOfAppointment: false,
    };
  }
  return {
    _hasPowerOfAppointment: true,
    power: powerOfAppointment,
    powerOtherNote: powerOfAppointmentNotes || '',
  };
}
